/**
 * Chess multiplayer room — opens a WebSocket to /api/chess/ws/:key,
 * authenticates with the visitor's display name, and renders the
 * canonical server FEN.
 *
 * Move-legality is checked locally with a chess.js instance kept
 * in sync with the server FEN.  When the local player moves we
 * apply it locally first (for instant feedback) and send {fen, san,
 * from, to, promotion?, end?, winner?} to the server for relay.
 *
 * Board orientation flips so each player always sees their own
 * pieces at the bottom.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Chess } from "chess.js";
import { Copy } from "lucide-react";
import { wsBase } from "@/lib/api";
import { playGemPlace } from "@/lib/sounds";
import TopBar from "@/components/TopBar";
import NavPane from "@/components/NavPane";
import { useEerieBackdrop } from "@/lib/eerie";
import ChessBoard3D from "@/components/Chess/ChessBoard3D";

const FILES = ["a", "b", "c", "d", "e", "f", "g", "h"];
const RANKS = [8, 7, 6, 5, 4, 3, 2, 1];
const ROLES = { p: "pawn", n: "knight", b: "bishop", r: "rook", q: "queen", k: "king" };
const FALLBACK_GLYPH = { p: "♟", n: "♞", b: "♝", r: "♜", q: "♛", k: "♚" };

const PAL = {
  bg:    "linear-gradient(180deg, #1B1A18 0%, #232220 60%, #181715 100%)",
  panel: "rgba(245,242,232,0.06)",
  border:"#C8A45A",
  accent:"#C8A45A",
  ink:   "#E8DDB8",
  muted: "#9A8E72",
  light: "#D7CDB1",
  dark:  "#3B342A",
  hi:    "rgba(232,221,184,0.55)",
  legal: "rgba(232,184,106,0.85)",
  check: "rgba(220, 38, 38, 0.55)",
};
const TITLE_FONT = "'Cinzel','Playfair Display','EB Garamond','Georgia',serif";

function PieceImage({ piece, size }) {
  const side = piece.color === "w" ? "white" : "black";
  const role = ROLES[piece.type];
  const [errored, setErrored] = useState(false);
  if (errored) {
    return (
      <span style={{
        fontSize: size * 0.85, lineHeight: 1,
        color: side === "white" ? "#F2E6BD" : "#1A1816",
        textShadow: side === "white" ? "0 1px 2px rgba(0,0,0,0.6)" : "0 1px 2px rgba(232,221,184,0.4)",
        userSelect: "none",
      }}>{FALLBACK_GLYPH[piece.type]}</span>
    );
  }
  return (
    <img src={`/chess-figures/${side}_${role}.png`} alt={`${side} ${role}`}
      onError={() => setErrored(true)} draggable={false}
      style={{
        // Fit the piece WITHIN the square cell — show the whole figure
        // (head to feet) with no cropping.  Width/height both clamp to
        // the cell; objectFit:contain preserves aspect ratio.  The PNG
        // has a transparent background so the underlying chessboard
        // tile shows through behind the figure.
        width: size, height: size, objectFit: "contain",
        pointerEvents: "none", userSelect: "none",
      }}
    />
  );
}

function Square({ file, rank, piece, isLight, isSelected, isLegal, isLastMove, isCheck, onClick, size }) {
  return (
    <div onClick={onClick} data-testid={`chess-sq-${file}${rank}`}
      style={{
        width: size, height: size, position: "relative", overflow: "hidden",
        background: isLight ? PAL.light : PAL.dark,
        cursor: piece || isLegal ? "pointer" : "default",
        boxShadow: isSelected ? `inset 0 0 0 3px ${PAL.hi}` : isLastMove ? `inset 0 0 0 2px ${PAL.hi}` : "none",
      }}>
      {isCheck && (
        <div style={{ position: "absolute", inset: 0,
          background: `radial-gradient(circle at 50% 50%, ${PAL.check} 0%, transparent 65%)`,
          pointerEvents: "none" }} />
      )}
      {piece && (
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
          <PieceImage piece={piece} size={size} />
        </div>
      )}
      {isLegal && !piece && (
        <div style={{ position: "absolute", left: "50%", top: "50%",
          width: size * 0.28, height: size * 0.28,
          transform: "translate(-50%, -50%)", borderRadius: "50%",
          background: PAL.legal, opacity: 0.55, pointerEvents: "none" }} />
      )}
      {isLegal && piece && (
        <div style={{ position: "absolute", inset: 4, border: `3px solid ${PAL.legal}`,
          borderRadius: 2, pointerEvents: "none" }} />
      )}
    </div>
  );
}

function CapturedTray({ pieces, side, size = 22 }) {
  const counts = {};
  for (const p of pieces) counts[p.type] = (counts[p.type] || 0) + 1;
  const order = ["q", "r", "b", "n", "p"];
  return (
    <div className="flex flex-wrap items-center gap-1" data-testid={`chess-captured-${side}`}
      style={{ minHeight: size + 8 }}>
      {order.flatMap((t) => {
        if (!counts[t]) return [];
        const piece = { type: t, color: side === "white" ? "b" : "w" };
        return Array.from({ length: counts[t] }, (_, i) => (
          <div key={`${t}-${i}`} style={{ width: size, height: size * 1.5, opacity: 0.9 }}>
            <PieceImage piece={piece} size={size} />
          </div>
        ));
      })}
      {pieces.length === 0 && (
        <span style={{ color: PAL.muted, fontSize: 11, fontStyle: "italic" }}>none</span>
      )}
    </div>
  );
}

function PromotionModal({ color, onChoose }) {
  const choices = ["q", "r", "b", "n"];
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 50, background: "rgba(0,0,0,0.75)",
      display: "flex", alignItems: "center", justifyContent: "center" }}
      data-testid="chess-promotion-modal">
      <div style={{ background: "#23211D", border: `2px solid ${PAL.border}`,
        padding: "20px 24px", borderRadius: 6,
        boxShadow: `0 8px 40px rgba(0,0,0,0.6), inset 0 0 0 1px ${PAL.accent}33` }}>
        <div className="text-xs tracking-[0.3em] uppercase mb-3"
          style={{ color: PAL.accent, fontFamily: TITLE_FONT }}>
          Promote pawn to:
        </div>
        <div className="flex gap-2">
          {choices.map((t) => (
            <button key={t} onClick={() => onChoose(t)}
              data-testid={`chess-promote-${t}`}
              data-silent="true"
              style={{
                width: 64, height: 96, background: "#1B1A18",
                border: `1px solid ${PAL.border}`, borderRadius: 4,
                cursor: "pointer", padding: 0, overflow: "hidden",
                display: "flex", alignItems: "flex-end", justifyContent: "center",
              }}>
              <PieceImage piece={{ type: t, color }} size={56} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ChessRoom() {
  useEerieBackdrop("estampie");
  const navigate = useNavigate();
  const { key } = useParams();
  const wsRef = useRef(null);
  const localGameRef = useRef(new Chess());

  const [state, setState] = useState(null);
  const [you, setYou] = useState(null);
  const [yourName, setYourName] = useState(null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);
  const [keyCopied, setKeyCopied] = useState(false);
  const [boardSize, setBoardSize] = useState(64);
  const [selected, setSelected] = useState(null);
  const [legalTo, setLegalTo] = useState([]);
  const [pendingPromo, setPendingPromo] = useState(null);

  const myName = useMemo(
    () => (localStorage.getItem("chess:name") || "Stranger").slice(0, 30),
    []
  );

  useEffect(() => {
    const onResize = () => {
      const w = window.innerWidth;
      setBoardSize(w <= 640 ? 36 : w <= 900 ? 44 : 64);
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Sync local chess.js instance with server FEN whenever state changes.
  useEffect(() => {
    if (state?.fen) {
      try { localGameRef.current.load(state.fen); } catch { /* ignore */ }
    }
  }, [state?.fen]);

  useEffect(() => {
    if (!key) return;
    let ws = null;
    let reconnectTimer = null;
    let pingTimer = null;
    let cancelled = false;
    let attempt = 0;
    let lastFen = "";
    const connect = () => {
      if (cancelled) return;
      const url = `${wsBase()}/chess/ws/${encodeURIComponent(key.toUpperCase())}`;
      ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onopen = () => {
        if (cancelled) return;
        setConnected(true);
        attempt = 0;
        ws.send(JSON.stringify({ type: "join", name: myName }));
        if (pingTimer) clearInterval(pingTimer);
        pingTimer = setInterval(() => {
          try { if (ws && ws.readyState === 1) ws.send(JSON.stringify({ type: "ping" })); } catch { /* */ }
        }, 25000);
      };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === "state") {
            // Fire a "piece on wood" tap whenever the FEN advances —
            // both the player who moved and the opponent hear it.
            if (lastFen && msg.fen && msg.fen !== lastFen) {
              try { playGemPlace(); } catch { /* */ }
            }
            lastFen = msg.fen || lastFen;
            setState(msg);
            setYou(msg.you);
            setYourName(msg.your_name);
            setSelected(null); setLegalTo([]);
          } else if (msg.type === "error") {
            setError(msg.error || "An error occurred.");
            if (msg.error !== "room_not_found") {
              setTimeout(() => setError(null), 2200);
            }
          }
        } catch { /* */ }
      };
      ws.onclose = () => {
        if (cancelled) return;
        setConnected(false);
        if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }
        attempt += 1;
        const wait = Math.min(5000, 400 * attempt);
        reconnectTimer = setTimeout(connect, wait);
      };
    };
    connect();
    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (pingTimer) clearInterval(pingTimer);
      try { ws && ws.close(); } catch { /* */ }
    };
  }, [key, myName]);

  const game = localGameRef.current;
  // Whose turn locally (derived from FEN)
  const turn = state?.fen ? state.fen.split(" ")[1] : "w";
  const myTurn = (you === "w" || you === "b") && turn === you && !state?.winner;

  const board = useMemo(() => {
    try { return game.board(); }
    catch { return Array.from({ length: 8 }, () => Array(8).fill(null)); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.fen]);

  const captured = useMemo(() => {
    const start = { p: 8, n: 2, b: 2, r: 2, q: 1, k: 1 };
    const onW = { p: 0, n: 0, b: 0, r: 0, q: 0, k: 0 };
    const onB = { p: 0, n: 0, b: 0, r: 0, q: 0, k: 0 };
    for (const row of board) for (const sq of row) {
      if (!sq) continue;
      (sq.color === "w" ? onW : onB)[sq.type]++;
    }
    const capByWhite = []; const capByBlack = [];
    for (const t of Object.keys(start)) {
      for (let i = 0; i < (start[t] - onB[t]); i++) capByWhite.push({ type: t, color: "b" });
      for (let i = 0; i < (start[t] - onW[t]); i++) capByBlack.push({ type: t, color: "w" });
    }
    return { capByWhite, capByBlack };
  }, [board]);

  const lastMove = state?.last_move || null;
  const checkSquare = useMemo(() => {
    try {
      if (!game.inCheck()) return null;
      const t = game.turn();
      for (let r = 0; r < 8; r++) for (let f = 0; f < 8; f++) {
        const sq = board[r][f];
        if (sq && sq.type === "k" && sq.color === t) return `${FILES[f]}${RANKS[r]}`;
      }
    } catch { /* */ }
    return null;
  }, [board, game]);

  const sendMove = (from, to, promotion) => {
    // Apply locally for instant feedback
    let mv = null;
    try { mv = game.move({ from, to, promotion }); } catch { mv = null; }
    if (!mv) return;
    // Detect end-of-game
    let end = null; let winner = null;
    if (game.isCheckmate()) { end = "checkmate"; winner = turn; /* mover */ }
    else if (game.isStalemate()) { end = "stalemate"; winner = "draw"; }
    else if (game.isThreefoldRepetition()) { end = "threefold"; winner = "draw"; }
    else if (game.isInsufficientMaterial()) { end = "insufficient"; winner = "draw"; }
    else if (game.isDraw()) { end = "50move"; winner = "draw"; }
    if (wsRef.current && wsRef.current.readyState === 1) {
      wsRef.current.send(JSON.stringify({
        type: "move", from, to, promotion: promotion || null,
        san: mv.san, fen: game.fen(), end, winner,
      }));
    }
  };

  const onSquareClick = (square) => {
    if (!myTurn) return;
    if (selected && selected === square) {
      setSelected(null); setLegalTo([]); return;
    }
    const piece = game.get(square);
    if (piece && piece.color === turn) {
      const moves = game.moves({ square, verbose: true });
      setSelected(square);
      setLegalTo(moves.map((m) => m.to));
      return;
    }
    if (!selected) return;
    const moves = game.moves({ square: selected, verbose: true });
    const cand = moves.find((m) => m.to === square);
    if (!cand) { setSelected(null); setLegalTo([]); return; }
    if (cand.promotion || (cand.flags && cand.flags.includes("p"))) {
      setPendingPromo({ from: selected, to: square, color: turn });
      return;
    }
    sendMove(selected, square);
    setSelected(null); setLegalTo([]);
  };
  const onPromote = (choice) => {
    if (!pendingPromo) return;
    sendMove(pendingPromo.from, pendingPromo.to, choice);
    setPendingPromo(null);
  };

  const resign = () => {
    if (!wsRef.current || wsRef.current.readyState !== 1) return;
    if (you !== "w" && you !== "b") return;
    if (state?.winner) return;
    if (!window.confirm("Resign this match?")) return;
    wsRef.current.send(JSON.stringify({ type: "resign" }));
  };
  const newGame = () => {
    if (!wsRef.current || wsRef.current.readyState !== 1) return;
    if (!state?.winner) return;
    wsRef.current.send(JSON.stringify({ type: "new_game" }));
  };
  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(key.toUpperCase());
      setKeyCopied(true);
      setTimeout(() => setKeyCopied(false), 1500);
    } catch { /* */ }
  };

  // Orientation: black's view flips so your pieces are at the bottom.
  const flipped = you === "b";
  const ranksOrdered = flipped ? [...RANKS].reverse() : RANKS;
  const filesOrdered = flipped ? [...FILES].reverse() : FILES;

  let statusLine = "Connecting…";
  let emphasise = false;
  if (error === "room_not_found") {
    statusLine = "Match not found. Return to the lobby."; emphasise = true;
  } else if (!connected) {
    statusLine = "Connecting…";
  } else if (!state) {
    statusLine = "Joining match…";
  } else if (state.winner === "draw") {
    statusLine = `Draw — ${state.end_reason || "agreed"}.`; emphasise = true;
  } else if (state.winner === "w" || state.winner === "b") {
    const winSide = state.winner === "w" ? "White" : "Black";
    const reason = state.end_reason || "checkmate";
    const myWin = (you === "w" || you === "b") && state.winner === you;
    statusLine = `${winSide} wins — ${reason}.${myWin ? "  (You win!)" : ""}`;
    emphasise = true;
  } else {
    const seatedW = !!state.seats?.w;
    const seatedB = !!state.seats?.b;
    if (!seatedW || !seatedB) {
      statusLine = "Waiting for opponent… Share the match-key.";
    } else {
      const turnSide = turn === "w" ? "White" : "Black";
      const turnName = state.seats?.[turn] || turnSide;
      let inCheck = false;
      try { inCheck = game.inCheck(); } catch { /* */ }
      statusLine = turn === you
        ? `Your turn (${turnSide})${inCheck ? " — check" : ""}.`
        : `${turnName} (${turnSide}) is to move${inCheck ? " — check" : ""}.`;
    }
  }

  const myColourLabel = you === "w" ? "White" : you === "b" ? "Black" : "Spectator";

  // Build pair-rows for history
  const historyPairs = [];
  const hist = state?.history || [];
  for (let i = 0; i < hist.length; i += 2) {
    historyPairs.push({ n: i / 2 + 1, w: hist[i], b: hist[i + 1] || "" });
  }

  return (
    <div className="relative min-h-screen overflow-x-clip"
      data-testid="chess-room-page"
      style={{ background: PAL.bg, color: PAL.ink, fontFamily: "'Inter','Helvetica Neue',sans-serif" }}>
      <TopBar theme="studio" onBack={() => navigate("/games/chess")} backLabel="LOBBY" testId="chess-room-back-button" />

      <div className="relative z-10 max-w-7xl mx-auto flex flex-col md:flex-row">
        <div className="md:w-[260px] md:shrink-0 md:min-h-screen flex flex-col pt-4 md:pt-6 px-4 md:px-6 pb-6 md:border-r" style={{ borderColor: `${PAL.accent}26` }}>
          <NavPane theme="studio" />
        </div>
        <main className="flex-1 px-6 md:px-12 pt-10 pb-16">
          <div className="text-[10px] tracking-[0.4em] uppercase mb-3" style={{ color: PAL.accent, fontFamily: TITLE_FONT }}>
            Multiplayer · Match {key?.toUpperCase()}
          </div>
          <h1 className="mb-2" data-testid="chess-room-title"
            style={{
              fontFamily: TITLE_FONT, fontWeight: 400, color: PAL.ink,
              fontSize: "clamp(1.6rem, 3vw, 2.4rem)", lineHeight: 1.05,
              letterSpacing: "0.005em",
              textShadow: "0 1px 0 rgba(0,0,0,0.4), 0 0 16px rgba(200,164,90,0.14)",
            }}>
            {state?.name || "Chess"}
          </h1>

          <div className="flex items-center gap-3 mb-6 flex-wrap">
            <div className="text-xs tracking-[0.3em] uppercase" style={{ color: PAL.muted, fontFamily: TITLE_FONT }}>
              Match-key
            </div>
            <div data-testid="chess-room-key" style={{
              background: PAL.panel, border: `1px solid ${PAL.border}`,
              padding: "4px 12px", letterSpacing: "0.4em", fontSize: 16,
              fontFamily: TITLE_FONT, color: PAL.ink,
            }}>{key?.toUpperCase()}</div>
            <button onClick={copyKey} data-testid="chess-copy-key"
              className="text-xs uppercase tracking-[0.2em] inline-flex items-center gap-1"
              style={{ color: PAL.accent, fontFamily: TITLE_FONT }}>
              <Copy size={12} /> {keyCopied ? "Copied!" : "Copy"}
            </button>
            <div className="flex-1" />
            <div className="text-xs" style={{ color: PAL.muted, fontFamily: TITLE_FONT }}>
              You: <span style={{ color: PAL.ink }}>{yourName || myName}</span>{" · "}{myColourLabel}
            </div>
          </div>

          <div className="flex flex-col gap-8">
            <div>
              {/* Opponent's captured pieces above */}
              <div className="mb-2 flex items-center gap-3">
                <span className="text-[10px] tracking-[0.3em] uppercase" style={{ color: PAL.muted, fontFamily: TITLE_FONT }}>
                  {flipped ? (state?.seats?.w || "White") : (state?.seats?.b || "Black")}
                </span>
                <CapturedTray pieces={flipped ? captured.capByBlack : captured.capByWhite} side={flipped ? "black" : "white"} />
              </div>

              <div data-testid="chess-board-wrap"
                style={{
                  width: "100%",
                  maxWidth: 1100,
                }}>
                <ChessBoard3D
                  board={board}
                  flipped={flipped}
                  selected={selected}
                  legalTo={legalTo}
                  lastMove={lastMove}
                  checkSquare={checkSquare}
                  onSquareClick={(sq) => onSquareClick(sq)}
                  onBackdropClick={() => { setSelected(null); setLegalTo([]); }}
                  height={Math.min(820, Math.max(560, boardSize * 12))}
                />
              </div>

              <div className="mt-2 flex items-center gap-3">
                <span className="text-[10px] tracking-[0.3em] uppercase" style={{ color: PAL.muted, fontFamily: TITLE_FONT }}>
                  {flipped ? (state?.seats?.b || "Black") : (state?.seats?.w || "White")}
                </span>
                <CapturedTray pieces={flipped ? captured.capByWhite : captured.capByBlack} side={flipped ? "white" : "black"} />
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <div data-testid="chess-status" className="text-sm"
                  style={{
                    color: emphasise ? PAL.ink : PAL.accent,
                    fontFamily: TITLE_FONT, letterSpacing: "0.05em",
                    fontWeight: emphasise ? 700 : 500,
                  }}>
                  {statusLine}
                </div>
                <div className="flex-1" />
                {state?.winner ? (
                  <button onClick={newGame} disabled={you !== "w" && you !== "b"}
                    data-testid="chess-new-game"
                    className="px-3 py-1.5 text-xs tracking-[0.25em] uppercase"
                    style={{
                      background: PAL.ink, color: "#1B1A18",
                      border: `1px solid ${PAL.border}`, fontWeight: 700, fontFamily: TITLE_FONT,
                      opacity: you !== "w" && you !== "b" ? 0.4 : 1,
                    }}>
                    New Game (swap colours)
                  </button>
                ) : (
                  <button onClick={resign} disabled={you !== "w" && you !== "b"}
                    data-testid="chess-resign"
                    className="px-3 py-1.5 text-xs tracking-[0.25em] uppercase"
                    style={{
                      background: "transparent", color: PAL.accent,
                      border: `1px solid ${PAL.border}`, fontWeight: 600, fontFamily: TITLE_FONT,
                      opacity: you !== "w" && you !== "b" ? 0.4 : 1,
                    }}>
                    Resign
                  </button>
                )}
              </div>

              {error && error !== "room_not_found" && (
                <div className="mt-3 text-xs" style={{ color: "#FF8B8B" }} data-testid="chess-room-error">
                  {error}
                </div>
              )}
            </div>

            {/* Per user iter 61: Move History relocated BELOW the
                interactive canvas (was a right-side rail). */}
            <div style={{ maxWidth: 1100 }}>
              <div className="text-[10px] tracking-[0.4em] uppercase mb-3" style={{ color: PAL.accent, fontFamily: TITLE_FONT }}>
                Move History
              </div>
              <div className="overflow-y-auto"
                style={{
                  background: PAL.panel, border: `1px solid ${PAL.border}`,
                  maxHeight: 320, padding: "10px 12px", fontFamily: TITLE_FONT,
                }}
                data-testid="chess-history">
                {historyPairs.length === 0 ? (
                  <div style={{ color: PAL.muted, fontStyle: "italic", fontSize: 13 }}>
                    No moves yet.  White to play.
                  </div>
                ) : (
                  <table style={{ width: "100%", fontSize: 13 }}>
                    <tbody>
                      {historyPairs.map((p) => (
                        <tr key={p.n}>
                          <td style={{ width: 32, color: PAL.muted, padding: "2px 4px" }}>{p.n}.</td>
                          <td style={{ padding: "2px 4px", color: PAL.ink }}>{p.w}</td>
                          <td style={{ padding: "2px 4px", color: PAL.ink }}>{p.b}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>

      {pendingPromo && (
        <PromotionModal color={pendingPromo.color} onChoose={onPromote} />
      )}
    </div>
  );
}
